import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_DEVICES_PIE,
  CHART_KEY_DASHBOARD_DEVICES_TABLE,
  CHART_KEY_DASHBOARD_DEVICES_TREND,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import { useState } from "react";
import {
  dashboardChartKey,
  revenueOrConversions,
  useIsEcommerceProject,
} from "features/dashboard/utils/ecommerceMetrics";

interface IDevicesTabProps {
  attributes: ApiChartAttribute[];
}

function DevicesTab({ attributes }: IDevicesTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const [metric, setMetric] = useState<ApiMetric>(
    ApiMetric.METRIC_SESSIONS_COUNT,
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}
      >
        <Box
          sx={{
            height: { xs: 320, md: 400 },
            width: { xs: "100%", md: "33.333%" },
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_DEVICES_PIE}
            staticAttributes={attributes}
            chart={{
              name: "Device Composition",
              dimension: [ApiDimension.DIMENSION_DEVICE],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [metric],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                showTotals: true,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            height: { xs: 320, md: 400 },
            flex: 1,
            minWidth: 0,
          }}
        >
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_DEVICES_TREND}
            staticAttributes={attributes}
            onChangeMetric={setMetric}
            chart={{
              name: "Device Type by",
              dimension: [
                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                ApiDimension.DIMENSION_DEVICE,
              ],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                  chart: {
                    [ApiMetric.METRIC_SESSIONS_COUNT]: ApiYAxisChartType.AREA,
                    [ApiMetric.METRIC_EFFECTIVE_SESSION_RATE]:
                      ApiYAxisChartType.LINE,
                    [ApiMetric.METRIC_MACRO_CONVERSION_RATE]:
                      ApiYAxisChartType.LINE,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                availableMetrics: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                ],
              },
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_DEVICES_TABLE,
            isEcommerce,
          )}
          staticAttributes={attributes}
          chart={{
            name: "Device & Browser Engagement",
            dimension: [ApiDimension.DIMENSION_DEVICE],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_MACRO_CONVERSION_RATE,
                  ApiMetric.METRIC_AVERAGE_PAGEVIEWS_PER_SESSION,
                  ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
                  revenueOrConversions(isEcommerce),
                ],
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
              availableDimensions: [
                ApiDimension.DIMENSION_DEVICE,
                ApiDimension.DIMENSION_BROWSER,
              ],
            },
          }}
        />
      </Box>
    </>
  );
}

export default DevicesTab;
